import React from 'react';
import { Play, Pause } from 'lucide-react';
import { useMusicPlayer } from '../context/MusicPlayerContext';
import './MiniPlayer.css';

interface MiniPlayerProps {
  // true: docked beside the accessibility button (non-chromeless routes,
  // which always render that button). false: floating bottom-left, for
  // chromeless routes (HomeV2) that have no accessibility button to dock
  // next to.
  dock: boolean;
}

const RADIUS = 19;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

// Small persistent play/pause control with a ring around its own
// perimeter showing playback progress. Only ever visible once the user
// has started music (via the full widget's "Turn Up") and while that
// full widget isn't itself on screen — the two never show at once.
// Deliberately no seeking: the ring is read-only progress, not a scrubber.
const MiniPlayer: React.FC<MiniPlayerProps> = ({ dock }) => {
  const { hasUserStartedMusic, fullPlayerVisible, isPlaying, progress, togglePlayback, currentTrack } = useMusicPlayer();

  if (!hasUserStartedMusic || fullPlayerVisible || !currentTrack) return null;

  const offset = CIRCUMFERENCE * (1 - Math.min(1, Math.max(0, progress)));

  return (
    <button
      type="button"
      className={`mini-player ${dock ? 'mini-player--dock' : 'mini-player--float'}`}
      onClick={togglePlayback}
      aria-label={isPlaying ? `Pause ${currentTrack.title}` : `Play ${currentTrack.title}`}
      title={`${currentTrack.title} — ${currentTrack.artist}`}
    >
      <svg className="mini-player-ring" width="44" height="44" viewBox="0 0 44 44">
        <circle
          className="mini-player-ring-track"
          cx="22"
          cy="22"
          r={RADIUS}
          fill="none"
          strokeWidth="2.5"
        />
        <circle
          className="mini-player-ring-progress"
          cx="22"
          cy="22"
          r={RADIUS}
          fill="none"
          strokeWidth="2.5"
          strokeDasharray={CIRCUMFERENCE}
          strokeDashoffset={offset}
          strokeLinecap="round"
          transform="rotate(-90 22 22)"
        />
      </svg>
      <span className="mini-player-icon">
        {isPlaying ? <Pause size={16} fill="currentColor" /> : <Play size={16} fill="currentColor" />}
      </span>
    </button>
  );
};

export default MiniPlayer;
